import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Navigation, MapPin, Check, Loader, Search } from 'lucide-react';
import { useLocation } from '../contexts/LocationContext';

const LocationPickerModal = ({ onClose }) => {
  const { setGeocodedLocation } = useLocation();
  const [selected, setSelected] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [mapReady, setMapReady] = useState(false);
  
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const autocompleteRef = useRef(null);
  const placesServiceRef = useRef(null);

  // Initialize Google Maps
  useEffect(() => {
    const initMap = () => {
      if (!window.google?.maps || !mapContainerRef.current) return;

      const defaultCenter = { lat: 0, lng: 0 }; // Will center on user's location when GPS activates

      mapRef.current = new window.google.maps.Map(mapContainerRef.current, {
        center: defaultCenter,
        zoom: 13,
        disableDefaultUI: true,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] }
        ]
      });

      markerRef.current = new window.google.maps.Marker({
        position: defaultCenter,
        map: mapRef.current,
        draggable: true,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 14,
          fillColor: '#FF6B35',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 3,
        }
      });

      // Click on map to move marker
      mapRef.current.addListener('click', (e) => {
        markerRef.current.setPosition(e.latLng);
        reverseGeocode(e.latLng.lat(), e.latLng.lng());
      });

      // Drag marker
      markerRef.current.addListener('dragend', () => {
        const pos = markerRef.current.getPosition();
        reverseGeocode(pos.lat(), pos.lng());
      });

      // Places Autocomplete
      autocompleteRef.current = new window.google.maps.places.AutocompleteService();
      placesServiceRef.current = new window.google.maps.places.PlacesService(mapRef.current);

      setMapReady(true);
    };

    if (window.google?.maps) {
      initMap();
    } else {
      const interval = setInterval(() => {
        if (window.google?.maps) {
          clearInterval(interval);
          initMap();
        }
      }, 100);
      setTimeout(() => clearInterval(interval), 10000);
    }
  }, []);

  // Reverse geocode coordinates to address
  const reverseGeocode = useCallback(async (lat, lng) => {
    if (!window.google?.maps) return;

    const geocoder = new window.google.maps.Geocoder();
    try {
      const response = await geocoder.geocode({ location: { lat, lng } });
      if (response.results?.[0]) {
        const result = response.results[0];
        const components = result.address_components || [];
        
        const city = components.find(c => 
          c.types.includes('locality') || 
          c.types.includes('sublocality') ||
          c.types.includes('administrative_area_level_2')
        )?.long_name || 'Location';
        
        const country = components.find(c => 
          c.types.includes('country')
        )?.long_name || '';

        setSelected({
          city,
          country,
          displayName: `${city}${country ? ', ' + country : ''}`,
          lat,
          lng,
          formattedAddress: result.formatted_address
        });
      }
    } catch (e) {
      console.error('Geocode error:', e);
    }
  }, []);

  // Handle search input
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (!query || query.length < 2 || !autocompleteRef.current) {
      setSuggestions([]);
      return;
    }

    autocompleteRef.current.getPlacePredictions(
      { input: query, types: ['(cities)'] },
      (predictions, status) => {
        if (status === 'OK' && predictions) {
          setSuggestions(predictions.slice(0, 5));
        } else {
          setSuggestions([]);
        }
      }
    );
  }, []);

  // Select a suggestion
  const handleSelectSuggestion = useCallback((placeId, description) => {
    if (!placesServiceRef.current) return;

    placesServiceRef.current.getDetails(
      { placeId, fields: ['geometry', 'formatted_address', 'address_components'] },
      (place, status) => {
        if (status === 'OK' && place?.geometry) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          mapRef.current?.setCenter({ lat, lng });
          mapRef.current?.setZoom(14);
          markerRef.current?.setPosition({ lat, lng });

          const components = place.address_components || [];
          const city = components.find(c => 
            c.types.includes('locality') || 
            c.types.includes('sublocality')
          )?.long_name || description.split(',')[0];
          
          const country = components.find(c => 
            c.types.includes('country')
          )?.long_name || '';

          setSelected({
            city,
            country,
            displayName: `${city}${country ? ', ' + country : ''}`,
            lat,
            lng,
            formattedAddress: place.formatted_address
          });
        }
        setSuggestions([]);
        setSearchQuery(description);
      }
    );
  }, []);

  // Use GPS
  const handleGPS = useCallback(async () => {
    if (!navigator.geolocation) return;
    
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        
        mapRef.current?.setCenter({ lat, lng });
        mapRef.current?.setZoom(15);
        markerRef.current?.setPosition({ lat, lng });
        
        await reverseGeocode(lat, lng);
        setGpsLoading(false);
      },
      () => setGpsLoading(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [reverseGeocode]);

  // Confirm selection
  const handleConfirm = useCallback(() => {
    if (!selected) return;
    setGeocodedLocation(selected);
    localStorage.removeItem('fq_location_prompt_dismissed');
    setConfirmed(true);
    setTimeout(onClose, 400);
  }, [selected, setGeocodedLocation, onClose]);

  return (
    <div className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="location-picker-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl overflow-hidden shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <MapPin className="w-5 h-5" />
            <h2 className="font-bold text-lg">Pick Your Location</h2>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search city or suburb..."
              className="w-full pl-10 pr-4 py-3 bg-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/50"
              data-testid="location-search-input"
            />
          </div>
          
          {/* Suggestions dropdown */}
          {suggestions.length > 0 && (
            <div className="absolute left-4 right-4 top-full mt-1 bg-white rounded-xl shadow-lg border border-gray-100 z-10 overflow-hidden">
              {suggestions.map((s) => (
                <button
                  key={s.place_id}
                  onClick={() => handleSelectSuggestion(s.place_id, s.description)}
                  className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-2 border-b last:border-0"
                >
                  <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="truncate">{s.description}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Google Map */}
        <div className="relative flex-1 min-h-[250px]">
          <div ref={mapContainerRef} className="w-full h-full min-h-[250px]" />
          
          {!mapReady && (
            <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
              <div className="text-center">
                <Loader className="w-8 h-8 text-[#FF6B35] animate-spin mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading map...</p>
              </div>
            </div>
          )}
          
          {/* GPS Button */}
          <button
            onClick={handleGPS}
            disabled={gpsLoading}
            className="absolute bottom-4 right-4 bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 transition-colors disabled:opacity-50"
            title="Use current location"
          >
            {gpsLoading ? (
              <Loader className="w-5 h-5 text-[#FF6B35] animate-spin" />
            ) : (
              <Navigation className="w-5 h-5 text-[#FF6B35]" />
            )}
          </button>
        </div>

        {/* Selected Location & Confirm */}
        <div className="p-4 border-t bg-gray-50">
          {selected ? (
            <div className="flex items-center gap-3 mb-3 p-3 bg-white rounded-xl border border-[#FF6B35]/20">
              <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-[#FF6B35]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{selected.city}</p>
                <p className="text-xs text-gray-500 truncate">{selected.formattedAddress || selected.displayName}</p>
              </div>
              <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
            </div>
          ) : (
            <div className="flex items-center gap-3 mb-3 p-3 bg-gray-100 rounded-xl text-gray-400">
              <MapPin className="w-5 h-5" />
              <span className="text-sm">Tap on map or search to select location</span>
            </div>
          )}

          <button
            onClick={handleConfirm}
            disabled={!selected || confirmed}
            className={`w-full py-3.5 rounded-2xl font-semibold text-white transition-all flex items-center justify-center gap-2 ${
              selected && !confirmed
                ? 'bg-gradient-to-r from-[#F4C430] to-[#FF6B35] hover:shadow-lg'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
            data-testid="confirm-location-btn"
          >
            {confirmed ? (
              <>
                <Check className="w-5 h-5" />
                Location Set!
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Confirm Location
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationPickerModal;
